import { ApiError, type Api, type PublicKeyCredentialCreationOptionsJSON } from "../api/client.js";
import type { AssertionOptions, Receipt } from "../api/types.js";
import { createThenSign, markPasskeyHere } from "./passkey.js";
import { isCancellation, signNow, webAuthnAvailable } from "./signing.js";

/**
 * DCTA-124 — signing a bank card that is not in the chat: 17's Check and sign for an add (§F.2 13e).
 *
 * The rules are the chat's (app/signing.ts, app/passkey.ts — the same functions, not a copy of them):
 *  - the ceremony's options are fetched before the tap (`prepare`), as the bank answers them: a passkey
 *    it knows → the payload's assertion options; none (CREDENTIAL_UNKNOWN) → registration options, and
 *    the one tap creates a passkey, then signs with it (§C.11);
 *  - the tap's FIRST asynchronous act is `credentials.get()` / `create()` — nothing awaited before it
 *    (Safari drops the user gesture across an await);
 *  - one ceremony at a time: a second tap while one is in flight does nothing;
 *  - a closed sheet is a state ("cancelled", "timed-out", "create-cancelled"), never an error.
 * What a result means for the screen is the screen's business.
 */
export type SignerApi = Pick<Api, "assertionOptions" | "registrationOptions" | "registrationVerify" | "confirm">;

interface KeyStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface SignerDeps {
  readonly api: SignerApi;
  readonly customerId: string;
  readonly credentials: CredentialsContainer;
  /** This device's own note that it holds a passkey the bank knows (passkey.ts). */
  readonly storage: KeyStore;
  /** Resolves when the document has focus (WebKit's get() needs it after the create sheet). */
  readonly focus: () => Promise<void>;
  readonly now: () => number;
  /** Can this phone hold a passkey at all? `webAuthnAvailable` unless a test says otherwise. */
  readonly available?: () => boolean;
}

/** What the tap will do: sign with a known passkey · create one and sign · nothing (no passkeys here). */
export type Prepared = "known" | "first" | "unsupported";

export type SignResult =
  | { readonly kind: "signed"; readonly receipt: Receipt }
  | { readonly kind: "cancelled" }
  | { readonly kind: "timed-out" }
  | { readonly kind: "create-cancelled" }
  /** The passkey was saved; the phone refused the second sheet — the card's own button, one more tap. */
  | { readonly kind: "saved" }
  | { readonly kind: "unsupported" }
  /** The bank says the payload's time ran out. */
  | { readonly kind: "expired" }
  | { readonly kind: "failed"; readonly error: unknown };

/** The bank says the payload's time ran out (CHALLENGE_EXPIRED, BUNDLE_EXPIRED …). */
export const isExpired = (error: unknown): boolean => error instanceof ApiError && /EXPIRED/.test(error.reasonCode);

export class CardSigner {
  readonly #d: SignerDeps;
  readonly #bundleId: string;
  #options: AssertionOptions | undefined;
  #reg: PublicKeyCredentialCreationOptionsJSON | undefined;
  #signing = false;

  constructor(deps: SignerDeps, bundleId: string) {
    this.#d = deps;
    this.#bundleId = bundleId;
  }

  /** A ceremony is in flight (the platform's sheet is up). */
  get signing(): boolean {
    return this.#signing;
  }

  /**
   * Before the tap: the options for the ceremony the tap will start. The bank answers the payload's
   * assertion options when it knows a passkey for this customer and CREDENTIAL_UNKNOWN when it knows none
   * — asked for every card, so a passkey made anywhere (the chat, another card) is the one this card uses.
   */
  async prepare(): Promise<Prepared> {
    if (!(this.#d.available ?? webAuthnAvailable)()) return "unsupported";
    try {
      this.#options = await this.#d.api.assertionOptions(this.#bundleId);
      return "known";
    } catch (error) {
      if (!(error instanceof ApiError && error.reasonCode === "CREDENTIAL_UNKNOWN")) throw error;
    }
    this.#reg = await this.#d.api.registrationOptions();
    return "first";
  }

  /**
   * The tap. Its FIRST asynchronous call is the ceremony. `undefined`: nothing to do — a ceremony is
   * in flight, or the options have not landed (the card's button is busy meanwhile).
   */
  sign(onSaved?: () => void): Promise<SignResult> | undefined {
    if (this.#signing) return undefined;
    const options = this.#options;
    const reg = this.#reg;
    if (options === undefined && reg === undefined) return undefined;
    this.#signing = true;
    const run = options !== undefined ? this.#withKnown(options) : this.#withNew(reg as PublicKeyCredentialCreationOptionsJSON, onSaved);
    return run.finally(() => {
      this.#signing = false;
    });
  }

  #withKnown(options: AssertionOptions): Promise<SignResult> {
    const started = this.#d.now();
    // FIRST asynchronous call of the tap: get().
    return signNow(this.#d.api, this.#bundleId, options, [], this.#d.credentials).then(
      (receipt): SignResult => {
        markPasskeyHere(this.#d.customerId, this.#d.storage);
        return { kind: "signed", receipt };
      },
      (error: unknown): SignResult => {
        if (isCancellation(error)) return { kind: this.#d.now() - started >= (options.timeout ?? 60_000) - 1000 ? "timed-out" : "cancelled" };
        if (isExpired(error)) return { kind: "expired" };
        return { kind: "failed", error };
      },
    );
  }

  #withNew(reg: PublicKeyCredentialCreationOptionsJSON, onSaved: (() => void) | undefined): Promise<SignResult> {
    // FIRST asynchronous call of the tap: createThenSign's first statement is create().
    return createThenSign(
      {
        api: this.#d.api,
        credentials: this.#d.credentials,
        focus: this.#d.focus,
        saved: () => {
          markPasskeyHere(this.#d.customerId, this.#d.storage);
          onSaved?.();
        },
        now: this.#d.now,
      },
      reg,
      this.#bundleId,
      [],
    ).then(
      (r): SignResult => {
        switch (r.kind) {
          case "signed":
            return { kind: "signed", receipt: r.receipt };
          case "sign-button":
            // Saved; the phone refused the second sheet. The next tap signs with get() alone — never an automatic retry.
            this.#reg = undefined;
            this.#options = r.options;
            return { kind: "saved" };
          case "unsupported":
            return { kind: "unsupported" };
          default:
            // create-cancelled · timed-out: as it was, with fresh registration options for the next tap.
            void this.#d.api.registrationOptions().then(
              (o) => {
                this.#reg = o;
              },
              () => undefined,
            );
            return { kind: r.kind };
        }
      },
      (error: unknown): SignResult => (isExpired(error) ? { kind: "expired" } : { kind: "failed", error }),
    );
  }
}
